import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './StoreDetail.css';

const API_BASE = process.env.REACT_APP_API_URL || 'https://saskararecord.com/api';

function formatPrice(price) {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    maximumFractionDigits: 0,
  }).format(price);
}

export default function StoreDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [activePhoto, setActivePhoto] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setNotFound(false);
    axios
      .get(`${API_BASE}/products/public/${slug}`)
      .then((res) => {
        if (mounted) {
          setProduct(res.data.data);
          const allPhotos = res.data.data.photos && res.data.data.photos.length > 0
            ? res.data.data.photos
            : [res.data.data.photo_url].filter(Boolean);
          setActivePhoto(allPhotos[0] || null);
          if (res.data.data.variants && res.data.data.variants.length > 0) {
            setSelectedVariant(res.data.data.variants[0].id);
          }
        }
      })
      .catch(() => {
        if (mounted) setNotFound(true);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [slug]);

  if (loading) {
    return <div className="store-detail__state">Memuat...</div>;
  }

  if (notFound || !product) {
    return (
      <div className="store-detail__state">
        <p>Produk tidak ditemukan.</p>
        <Link to="/" className="store-detail__back">← Kembali ke Beranda</Link>
      </div>
    );
  }

  const outOfStock = product.stock <= 0;

  const pageTitle = `${product.name} — Saskara Record Store`;
  const pageDesc = product.description || `${product.name}, merchandise resmi dari Saskara Record.`;
  const pageImage = product.photo_url || 'https://saskararecord.com/logo512.png';
  const pageUrl = `https://saskararecord.com/store/${slug}`;

  return (
    <>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDesc} />
      <meta property="og:type" content="product" />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDesc} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:url" content={pageUrl} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDesc} />
      <meta name="twitter:image" content={pageImage} />
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: product.name,
          description: product.description || undefined,
          image: product.photo_url || undefined,
          offers: {
            '@type': 'Offer',
            price: product.price || undefined,
            priceCurrency: 'IDR',
            availability: product.stock > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
          },
          url: pageUrl,
        })}
      </script>
    <div className="store-detail">
      <nav className="store-detail__nav">
        <Link to="/" className="store-detail__back">← Beranda</Link>
      </nav>

      <div className="store-detail__layout">
        <div>
          <div className="store-detail__photo">
            {activePhoto ? (
              <img src={activePhoto} alt={product.name} />
            ) : (
              <div className="store-detail__photo-fallback" />
            )}
          </div>
          {product.photos && product.photos.length > 1 && (
            <div className="store-detail__thumbs">
              {product.photos.map((url, i) => (
                <button
                  key={i}
                  className={`store-detail__thumb ${activePhoto === url ? 'is-active' : ''}`}
                  onClick={() => setActivePhoto(url)}
                >
                  <img src={url} alt={`${product.name} ${i + 1}`} />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="store-detail__info">
          {product.artist_name && (
            <Link to={`/artists/${product.artist_slug}`} className="store-detail__artist">
              {product.artist_name}
            </Link>
          )}
          <h1 className="store-detail__name">{product.name}</h1>
          <div className="store-detail__price">{formatPrice(product.price)}</div>

          {product.description && (
            <p className="store-detail__desc">{product.description}</p>
          )}

          {product.variants && product.variants.length > 0 && (
            <div className="store-detail__variants">
              <span className="store-detail__variants-label">Varian</span>
              <div className="store-detail__variants-list">
                {product.variants.map((v) => (
                  <button
                    key={v.id}
                    className={`store-detail__variant-btn ${selectedVariant === v.id ? 'is-active' : ''}`}
                    onClick={() => setSelectedVariant(v.id)}
                  >
                    {v.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <button
            className="store-detail__buy"
            disabled={outOfStock}
            onClick={() =>
              navigate('/checkout', {
                state: {
                  items: [{
                    product_id: product.id,
                    variant_id: selectedVariant,
                    quantity: 1,
                    name: product.name,
                    price: product.price,
                    photo_url: activePhoto,
                  }],
                },
              })
            }
          >
            {outOfStock ? 'Stok Habis' : 'Beli Sekarang'}
          </button>

          {!outOfStock && (
            <p className="store-detail__stock">Stok tersedia: {product.stock}</p>
          )}
        </div>
      </div>
    </div>
    </>
  );
}
